import { StoreObjectsAction } from '../actions/storeObjectActions';
import Auction from '../interfaces/Auction';
import { ReduxStore } from '../interfaces/store';
import { StoreObjectsState } from '../interfaces/store/StoreObject';
import { unionBy } from '~/helpers/unionBy';

export const initialState: StoreObjectsState = {
  entities: {},
  ids: [],
  filtered: {},
  isLoading: false,
  error: null,
  dynamicFilterOptions: {
    car: {
      brands: [],
      familyNames: {}
    },
    heavy_transport: {
      brands: [],
      familyNames: {}
    },
    light_transport: {
      brands: [],
      familyNames: {}
    },
    machines: {
      brands: [],
      familyNames: {}
    },
    recreational: {
      brands: [],
      familyNames: {}
    },
    transport_and_machinery: {
      brands: [],
      familyNames: {}
    },

    // TODO: [KVDBIL-1213] remove on split of vehicleTypes
    transport_vehicle: {
      brands: [],
      familyNames: {}
    },
    // TODO: [KVDBIL-1213] remove on renaming of vehicleTypes
    recreational_vehicle: {
      brands: [],
      familyNames: {}
    }
  },
  facilityFilterOptions: [],
  scrollPosition: {
    isRememberScrollActive: false,
    auctionId: null,
    url: null
  }
};

const mergeArrays = <T>(state: T[] = [], actionArray: T[] = []) =>
  [...new Set([...state, ...actionArray])] as T[];

const withFavouriteCount = (
  state: StoreObjectsState,
  auctionId: string,
  getCount: (favouriteCount: number) => number
): StoreObjectsState => {
  const auction = state.entities[auctionId];

  if (!auction) {
    return state;
  }

  return {
    ...state,
    entities: {
      ...state.entities,
      [auctionId]: {
        ...auction,
        favouriteCount: getCount(auction.favouriteCount ?? 0)
      }
    }
  };
};

const storeObjectsReducer = (
  state = initialState,
  action: StoreObjectsAction
): ReduxStore['storeObjects'] => {
  switch (action.type) {
    case 'FETCH_STORE_ITEMS_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'FETCH_STORE_ITEMS_SUCCESS':
      const allEntities = { ...state.entities, ...action.payload.entities };
      const allIds = mergeArrays<string>(state.ids, action.payload.filters.ids); // Ensure no duplicate IDs
      const filteredIds = mergeArrays<string>(
        state.filtered[action.payload.searchParamKey]?.ids,
        action.payload.filters.ids
      );

      // recalculate hits and total
      const previewIds =
        state.filtered[action.payload.searchParamKey]?.ids ?? [];
      const newlyIds = action.payload.filters.ids ?? [];
      const duplicateHits = newlyIds.filter(id =>
        previewIds.includes(id)
      ).length;

      // This should prevent backends lies about hits in pagination
      const nextHits = Math.max(
        action.payload.filters.hits - duplicateHits,
        filteredIds.length
      );

      // Populate entities, ids, and filters from action.payload
      return {
        ...state,
        entities: allEntities,
        ids: allIds, // Ensure no duplicate IDs
        filtered: {
          ...state.filtered,
          [action.payload.searchParamKey]: {
            ...state.filtered[action.payload.searchParamKey],
            ids: filteredIds,
            hits: nextHits,
            total: action.payload.filters.total
          }
        },
        isLoading: false,
        error: null
      };
    case 'FETCH_STORE_ITEMS_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'CLEAR_STORE_ITEMS_ERROR':
      return {
        ...state,
        error: null
      };

    case 'FETCH_DYNAMIC_FILTERS_SUCCESS':
      return {
        ...state,
        dynamicFilterOptions: {
          ...state.dynamicFilterOptions,
          [action.payload.mainCategory]: {
            brands: action.payload.brands ?? [],
            familyNames: action.payload.familyNames ?? {}
          }
        }
      };

    case 'FETCH_FACILITIES_SUCCESS':
      return {
        ...state,
        facilityFilterOptions: action.payload
      };

    case 'FILTER_PAGE_REMEMBER_SCROLL_ACTIVE':
      return {
        ...state,
        scrollPosition: {
          ...state.scrollPosition,
          isRememberScrollActive: action.payload
        }
      };

    case 'FILTER_PAGE_SET_SCROLL_DATA':
      return {
        ...state,
        scrollPosition: {
          ...state.scrollPosition,
          auctionId: action.payload.auctionId ?? null,
          url: action.payload.url ?? null
        }
      };

    // Websocket events
    case 'AUCTION_COUNTDOWN_STARTED': {
      const { auctionId, countdownDuration } = action.payload;
      const auction: Auction = state.entities[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          [auctionId]: {
            ...auction,
            countdownDuration: countdownDuration,
            activeAuction: {
              ...auction.activeAuction,
              countdownInProgress: true
            }
          }
        }
      };
    }
    case 'AUCTION_CLOSED': {
      const { auctionId, highestBid } = action.payload;
      const auction: Auction = state.entities[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      const prevBids = auction.activeAuction?.bids ?? [];
      const newBids = unionBy([...prevBids, highestBid], 'id');

      return {
        ...state,
        entities: {
          ...state.entities,
          [auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              auctionId,
              closed: true,
              countdownInProgress: false,
              highestBid,
              bids: newBids
            }
          }
        }
      };
    }
    case 'AUCTION_BID_PLACED': {
      const auction: Auction = state.entities[action.payload.auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      const oldBids = auction.activeAuction.bids;
      const bidFromEvent = {
        ...action.payload,
        auctionId: Number(action.payload.auctionId)
      };

      return {
        ...state,
        entities: {
          ...state.entities,
          [action.payload.auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              reservationPriceReached: action.payload.reservationPriceReached,
              bids: [bidFromEvent, ...oldBids]
            }
          }
        }
      };
    }
    case 'AUCTION_BID_CANCELLED': {
      const { auctionId } = action.payload;
      const auction: Auction = state.entities[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      const bids = auction.activeAuction.bids ?? [];
      const newBids = bids.filter(bid => bid.id !== action.payload.id);

      return {
        ...state,
        entities: {
          ...state.entities,
          [auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              reservationPriceReached: action.payload.reservationPriceReached,
              bids: newBids
            }
          }
        }
      };
    }
    case 'AUCTION_CLOSE_TIME_UPDATED': {
      const { auctionId, preliminaryCloseAt } = action.payload;
      const auction: Auction = state.entities[auctionId];

      if (!auction || !auction.activeAuction) {
        return state;
      }

      return {
        ...state,
        entities: {
          ...state.entities,
          [auctionId]: {
            ...auction,
            activeAuction: {
              ...auction.activeAuction,
              preliminaryCloseAt
            }
          }
        }
      };
    }

    case 'STORE_FAVOURITE_AUCTION_REQUEST':
    case 'REMOVE_FAVOURITE_AUCTION_FAILURE':
      return withFavouriteCount(
        state,
        action.payload,
        favouriteCount => favouriteCount + 1
      );

    case 'REMOVE_FAVOURITE_AUCTION_REQUEST':
    case 'STORE_FAVOURITE_AUCTION_FAILURE':
      return withFavouriteCount(state, action.payload, favouriteCount =>
        Math.max(0, favouriteCount - 1)
      );

    default:
      return state;
  }
};

export default storeObjectsReducer;
